import React, { useEffect, useMemo, useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import cmdGreetParents from '../assets/screens/game/cmd-comprimentar-pais.jpg';
import cmdGreetFriend from '../assets/screens/game/cmd-comprimentar-amigo.jpg';
import optBomDia from '../assets/screens/game/opt-bomdia.jpg';
import optBoaTarde from '../assets/screens/game/opt-boatarde.jpg';
import optBoaNoite from '../assets/screens/game/opt-boanoite.jpg';
import optComFome from '../assets/screens/game/opt-comfome.jpg';
import optOiTudoBem from '../assets/screens/game/opt-oitudobem.jpg';
import optCareta from '../assets/screens/game/opt-careta.jpg';
import optAtrasado from '../assets/screens/game/opt-atrasado.jpg';
import optIgnorar from '../assets/screens/game/opt-ignorar.jpg';

interface Option {
  label: string;
  image: string;
}

interface Round {
  commandImage: string;
  command: string;
  options: Option[];
  correctIndex: number;
}

// Comandos, imagens e opções são os mesmos das duas primeiras rodadas reais
// do Jogo 1 (ver SampleGame1.kt + os drawables command0_g1/command1_g1 e
// option*_g1 do projeto Android), na mesma posição de correctAnswerIndex
// usada pelo jogo.
const ROUNDS: Round[] = [
  {
    commandImage: cmdGreetParents,
    command: 'Como comprimentar seus pais ao acordar?',
    options: [
      { label: 'Boa tarde!', image: optBoaTarde },
      { label: 'Boa noite!', image: optBoaNoite },
      { label: 'Bom dia!', image: optBomDia },
      { label: 'Estou com fome!', image: optComFome },
    ],
    correctIndex: 2,
  },
  {
    commandImage: cmdGreetFriend,
    command: 'Você encontra um amigo na escola. Como você o comprimenta?',
    options: [
      { label: 'Oi, tudo bem?', image: optOiTudoBem },
      { label: 'Fazer careta.', image: optCareta },
      { label: 'Você está atrasado?', image: optAtrasado },
      { label: 'Ignorar.', image: optIgnorar },
    ],
    correctIndex: 0,
  },
];

type Phase = 'question' | 'reveal' | 'correct';

interface Frame {
  roundIndex: number;
  phase: Phase;
  duration: number;
}

function buildFrames(): Frame[] {
  const frames: Frame[] = [];
  ROUNDS.forEach((_, roundIndex) => {
    frames.push({ roundIndex, phase: 'question', duration: 1700 });
    frames.push({ roundIndex, phase: 'reveal', duration: 1200 });
    frames.push({ roundIndex, phase: 'correct', duration: 1700 });
  });
  return frames;
}

/** Simula uma rodada real do jogo de habilidades sociais: pergunta, opções com as fotos reais do app, acerto destacado e o feedback "Você Acertou!!" (texto literal do dialog_correct_game.xml). */
export function GameLiveDemo() {
  const frames = useMemo(buildFrames, []);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const frame = frames[frameIndex];
    const timer = setTimeout(() => setFrameIndex((i) => (i + 1) % frames.length), frame.duration);
    return () => clearTimeout(timer);
  }, [frameIndex, frames, prefersReducedMotion]);

  const current = prefersReducedMotion ? { roundIndex: 0, phase: 'correct' as Phase } : frames[frameIndex];
  const round = ROUNDS[current.roundIndex];

  return (
    <div className="rounded-2xl bg-card shadow-lg overflow-hidden" aria-hidden="true">
      <div className="bg-warm/15 dark:bg-warm/20 px-3.5 py-3 flex items-center gap-3">
        <img src={round.commandImage} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0 shadow-sm" />
        <p className="text-sm font-bold text-foreground leading-snug">{round.command}</p>
      </div>

      <div className="p-3 grid grid-cols-2 gap-2">
        {round.options.map((opt, i) => {
          const isCorrect = i === round.correctIndex;
          const showCorrect = current.phase !== 'question' && isCorrect;
          const dim = current.phase !== 'question' && !isCorrect;
          return (
            <div
              key={opt.label}
              className={`relative rounded-xl overflow-hidden transition-all duration-500 ${
                showCorrect ? 'ring-2 ring-[#4CAF50] scale-[1.03]' : dim ? 'opacity-40' : ''
              }`}
            >
              <img src={opt.image} alt="" className="w-full aspect-square object-cover" />
              {showCorrect && (
                <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#4CAF50] flex items-center justify-center shadow">
                  <Check className="w-3 h-3 text-white flex-shrink-0" />
                </span>
              )}
              <div
                className={`px-1.5 py-1 text-[11px] font-semibold text-center leading-tight transition-colors duration-500 ${
                  showCorrect ? 'bg-[#4CAF50] text-white' : 'bg-muted text-foreground'
                }`}
              >
                {opt.label}
              </div>
            </div>
          );
        })}
      </div>

      <div className="h-11 flex items-center justify-center pb-1">
        {current.phase === 'correct' && (
          <div className="flex items-center gap-1.5 text-sm font-bold text-white bg-[#4CAF50] rounded-full px-4 py-1.5 animate-scale-in">
            <Sparkles className="w-4 h-4 flex-shrink-0" />
            Você Acertou!!
          </div>
        )}
      </div>
    </div>
  );
}
